import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  PersonalDataSection,
  PlaceOfBirthSection,
  CitizenshipSection,
  TaxResidencySection,
  PepConnectionSection,
  ContactsSection,
  ActivitySection,
  IdentityDocumentSection,
  AdditionalDocumentsSection,
  KYCSection,
  ResidentialAddressSection,
  CompanyNameSection,
  CompanyStateRegistrationSection,
  CompanyRegistrationAddressSection,
  CompanyResidentialAddressSection,
  RegistrationAddressSection,
  BiographySection,
} from 'modules/client/common/components/sections';
import {
  CompanyConstituentDocumentsSection,
  CompanyDateOfStructureChangeSection,
} from 'modules/client/legal/components/sections';

import { CompanyTaxResidencySection, RoleInCompanySection } from '../sections';

import { useGetLegalRepresentativeInfo } from './hooks';
import { LegalRepresentativeSectionsProps } from './types';

export const useLegalRepresentativeSections = ({
  countries,
  countriesLoading,
  kycData,
  hideActivitySection,
  mainInfo,
  representativeType = 'physical',
}: LegalRepresentativeSectionsProps) => {
  const { t } = useTranslation();

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const isCompany = representativeType === 'company';

  const info = useGetLegalRepresentativeInfo(mainInfo, t, isCompany);

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legalRepresentative.sections.main'),
          id: 'main',
          content: (
            <>
              <PersonalDataSection enabledGender={isGlobal} />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <IdentityDocumentSection
                enabledDocuments
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
                enabledDocuments={isGlobal}
              />
              <PlaceOfBirthSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t(
            `client.legalRepresentative.sections.representativeInfo.${representativeType}`
          ),
          id: 'representativeInfo',
          content: (
            <>
              <RoleInCompanySection
                enabledCivilLawSubject={isFinance}
                title={t(
                  `client.legalRepresentative.roleInCompany.title.${representativeType}`
                )}
              />
              {isFinance && isCompany && (
                <>
                  <CompanyNameSection />
                  <CompanyStateRegistrationSection />
                  <CompanyTaxResidencySection
                    countries={countries}
                    countriesLoading={countriesLoading}
                  />
                  <CompanyRegistrationAddressSection
                    countries={countries}
                    countriesLoading={countriesLoading}
                  />
                  <CompanyResidentialAddressSection
                    countries={countries}
                    countriesLoading={countriesLoading}
                  />
                  <CompanyDateOfStructureChangeSection />
                </>
              )}
            </>
          ),
          formSectionKeys: [
            'roleInCompany',
            'companyName',
            'companyStateRegistration',
            'companyTaxResidences',
            'companyRegistrationAddress',
            'companyResidentialAddress',
            'constituentDocsDateOfLastChange',
          ],
        },
        {
          label: t('client.legalRepresentative.sections.contacts'),
          id: 'contacts',
          content: (
            <>
              {isFinance && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection />
            </>
          ),
          formSectionKeys: [
            'registrationAddress',
            'residentialAddress',
            'contacts',
          ],
        },
        {
          label: t('client.legalRepresentative.sections.documents'),
          id: 'documents',
          content: (
            <>
              {isFinance && isCompany && <CompanyConstituentDocumentsSection />}
              <AdditionalDocumentsSection />
            </>
          ),
          formSectionKeys: ['constituentDocsFiles', 'additionalDocumentsFiles'],
        },
        {
          label: t('client.legalRepresentative.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <KYCSection kycData={kycData} />
              <PepConnectionSection notRequestInformation={isGlobal} />
            </>
          ),
          formSectionKeys: ['additionalDocumentsFiles', 'pepConnection'],
        },
        {
          label: t('client.legalRepresentative.sections.other'),
          id: 'other',
          content: (
            <>
              {!hideActivitySection && <ActivitySection />}
              {isFinance && <BiographySection />}
            </>
          ),
          formSectionKeys: ['activity', 'biography'],
        },
      ]),
    [
      countries,
      countriesLoading,
      hideActivitySection,
      isCompany,
      isFinance,
      isGlobal,
      kycData,
      representativeType,
      t,
    ]
  );

  return { tabs, info };
};
